import * as React from 'react';

import type { ForcedResignatureDto } from '@/api/legal-admin-client';
import { useForcedResignatures } from '@/api/use-legal-admin';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateTime } from '@/routes/admin/cgu/cgu-format';

// =============================================================================
// forced-resignature-journal.tsx — Encart « Historique des re-signatures forcées » (R15-GOV-04).
// =============================================================================
// DBU §2.9 l.2136-2144 : la JOURNALISATION du motif est un champ de CONFORMITÉ admin-relisible.
// Cet encart RESTITUE le journal (GET /admin/legal/cgu/resignature/:siret) pour le SIRET de la fiche :
// motif EN CLAIR, date de forçage (FR), administrateur (id opaque tronqué comme ailleurs). L'écran est
// déjà ADMIN-ONLY : le motif n'est PAS masqué (ce n'est pas une PII à occulter côté admin, il DOIT être
// lisible). LECTURE SEULE (aucune mutation ici — le forçage vit dans ForceResignatureAction).
//
// WCAG 2.1 AA : section nommée (aria-labelledby), tableau avec caption sr-only + th scope=col,
// libellés porteurs, empty state porté par un texte (role=status). Anti-LLMism : pas d'em-dash de
// substitution dans les textes FR (parenthèses / virgules).

export interface ForcedResignatureJournalSectionProps {
  readonly siret: string;
}

/**
 * Encart admin listant l'historique des re-signatures forcées d'un SIRET. Trois états : chargement,
 * erreur (message serveur relayé), et données (tableau) ou empty state accessible si aucune entrée.
 * L'ordre « plus récent en tête » est l'AUTORITÉ SERVEUR (aucun tri UI).
 */
export function ForcedResignatureJournalSection({
  siret,
}: ForcedResignatureJournalSectionProps): React.JSX.Element {
  const journal = useForcedResignatures(siret);

  return (
    <section
      aria-labelledby="forced-resignature-heading"
      className="space-y-4"
    >
      <h2 id="forced-resignature-heading" className="text-lg font-medium">
        Historique des re-signatures forcées
      </h2>
      <Card>
        <CardContent className="py-6">
          {journal.isPending ? (
            <p role="status" className="text-muted-foreground">
              Chargement de l'historique…
            </p>
          ) : journal.isError ? (
            <p role="alert" className="text-destructive">
              {journal.error.message}
            </p>
          ) : journal.data.length === 0 ? (
            <p role="status" className="text-muted-foreground">
              Aucune re-signature forcée pour ce fournisseur.
            </p>
          ) : (
            <ForcedResignatureTable rows={journal.data} />
          )}
        </CardContent>
      </Card>
    </section>
  );
}

interface ForcedResignatureTableProps {
  readonly rows: readonly ForcedResignatureDto[];
}

/**
 * Tableau LECTURE SEULE du journal (rendu dans l'ordre reçu = « plus récent d'abord » serveur).
 * WCAG : caption sr-only, th scope=col ; le MOTIF est affiché EN CLAIR (champ de conformité), la
 * DATE en format FR, l'ADMINISTRATEUR via son nom RÉSOLU côté serveur (`actorLabel` = « Prénom Nom »,
 * ou « — » si non résolu ; DEC-47). L'UUID subject ne franchit plus la frontière front (R-5.1).
 */
function ForcedResignatureTable({
  rows,
}: ForcedResignatureTableProps): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Historique des re-signatures des CGU forcées pour ce fournisseur (motif,
          date, administrateur), de la plus récente à la plus ancienne
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <th scope="col" className="px-4 py-3 font-medium">
              Motif
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Date
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Administrateur
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.logId}
              className="border-b border-border last:border-b-0"
            >
              {/* Motif EN CLAIR : champ de conformité admin-relisible (DBU §2.9), jamais masqué. */}
              <td className="px-4 py-3 align-top">{row.motif}</td>
              <td className="px-4 py-3 align-top whitespace-nowrap">
                {formatDateTime(row.forcedAt)}
              </td>
              {/* DEC-47 — nom d'administrateur RÉSOLU côté serveur (« Prénom Nom » ou « — »). Plus
                  d'UUID tronqué : le subject ne franchit plus la frontière front (R-5.1). */}
              <td className="px-4 py-3 align-top text-muted-foreground">{row.actorLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
